import React, { Fragment, useEffect, useState } from "react";
import {
  Button,
  Grid,
  Alert,
  Autocomplete,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material/";
import { NavLink } from "react-router-dom";
import {
  Work,
  AccountBox,
  Inventory2,
  PersonAdd,
  Loop,
  AccessTime,
  Check,
  Close,
  Person2TwoTone,
  Person3Outlined,
  People,
  Info,
} from "@mui/icons-material";
import { CustomIcons, statusConfig } from "../../Components/helpers/Helpers";
import { CustomIconsPlusText } from "../../Components/Common/Design/CustomIconsPlusText";
import Swal from "sweetalert2";
import api from "../../api/axios";
import { COLORS, STYLES } from "./muiStyles/muiStyles";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";
import Star from "@mui/icons-material/Star";

const InfoSection = ({
  lawyerData,
  clientData,
  dataCase,
  contactConditionIfExist,
  lawyersList,
  setUpdatedCase,
  updatedCase,
  glassSurface,
  isCaseLoaded = false,
}) => {
  const [changeLawyer, setChangeLawyer] = useState(false);
  const [selectedLawyer, setSelectedLawyer] = useState(lawyerData || null);
  const [newLawyer, setNewLawyer] = useState(null);
  const [inputLawyerValue, setInputLawyerValue] = useState("");
  const [openMandatoryLawyerDialog, setOpenMandatoryLawyerDialog] =
    useState(false);
  const [isRefreshingLawyerInfo, setIsRefreshingLawyerInfo] = useState(false);
  const [pendingLawyerId, setPendingLawyerId] = useState(null);

  const hasAssignedLawyer = Boolean(
    selectedLawyer?.id && Number(selectedLawyer.id) > 0,
  );
  const caseCompanyId = Number(
    dataCase?.company_relation?.id || dataCase?.company_relation?.company_id,
  );
  const companyLawyersList = (lawyersList || []).filter((lawyer) => {
    if (!caseCompanyId) return true;
    return Number(lawyer?.company_id) === caseCompanyId;
  });

  const flagVip =
    contactConditionIfExist?.toLowerCase().includes("vip") || false;

  useEffect(() => setSelectedLawyer(lawyerData || null), [lawyerData]);

  useEffect(() => {
    if (!isRefreshingLawyerInfo) return;

    const isExpectedLawyer =
      pendingLawyerId == null ||
      Number(selectedLawyer?.id) === Number(pendingLawyerId);

    const hasCompleteData =
      Boolean(selectedLawyer?.name) && selectedLawyer?.company !== undefined;

    if (isExpectedLawyer && hasCompleteData) {
      setIsRefreshingLawyerInfo(false);
      setPendingLawyerId(null);
    }
  }, [isRefreshingLawyerInfo, pendingLawyerId, selectedLawyer]);

  useEffect(() => {
    if (!isCaseLoaded) {
      setOpenMandatoryLawyerDialog(false);
      setChangeLawyer(false);
      return;
    }

    // Solo abre el modal si el caso está cargado Y no hay abogado asignado
    // Pero verifica que lawyerData haya sido procesado (no sea undefined)

    const shouldOpenDialog = !hasAssignedLawyer && lawyerData !== undefined;

    setOpenMandatoryLawyerDialog(shouldOpenDialog);

    if (!hasAssignedLawyer) {
      setChangeLawyer(false);
      setNewLawyer(null);
      setInputLawyerValue("");
    }
  }, [hasAssignedLawyer, isCaseLoaded, lawyerData]);

  const handleConfirmNewLawyer = async () => {
    if (!newLawyer) return;

    // 1. Confirmación
    const confirm = await Swal.fire({
      title: "¿Guardar nuevo abogado?",
      text: `¿Deseas asignar a ${newLawyer.name} a este caso?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      // 2. Loading real
      Swal.fire({
        title: "Guardando...",
        text: "Procesando la información.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // console.log(dataCase?.id, newLawyer.id);

      const res = await api.post("cases/update", {
        id: dataCase?.id,
        user_id: newLawyer.id,
      });

      // console.log(res, "res");

      Swal.close();

      // 4. Éxito
      await Swal.fire({
        title: "Abogado actualizado",
        text: "El abogado ha sido asignado correctamente.",
        icon: "success",
        confirmButtonColor: "#1ab394",
        timer: 1000,
      });

      // Limpiar estados
      setIsRefreshingLawyerInfo(true);
      setPendingLawyerId(newLawyer?.id || null);
      setSelectedLawyer(newLawyer);
      setChangeLawyer(false);
      setNewLawyer(null);
      setInputLawyerValue("");
      setOpenMandatoryLawyerDialog(false);
      setUpdatedCase(!updatedCase);
    } catch (error) {
      // console.error(error);
      Swal.close();

      Swal.fire({
        title: "Error",
        text: "No se pudo guardar el abogado. Intenta nuevamente.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleCancel = () => {
    if (openMandatoryLawyerDialog) return;

    setNewLawyer(null);
    setInputLawyerValue("");
    setChangeLawyer(false);
  };

  const dataLawyer = [
    {
      label: "Abogado:",
      value: (
        <>
          {selectedLawyer?.id ? (
            <NavLink
              to={`/usuarios/ver?id=${selectedLawyer.id}`}
              style={{ textDecoration: "none" }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 700, color: COLORS.lawyer, fontSize: 14 }}
              >
                {`${selectedLawyer.name || ""} ${selectedLawyer.surname || ""}`.trim() ||
                  selectedLawyer.name}
              </Box>
            </NavLink>
          ) : (
            <Box component="span" sx={{ color: COLORS.subtleText }}>
              Sin asignar
            </Box>
          )}
        </>
      ),
    },
    { label: "Empresa:", value: selectedLawyer?.company || "-" },
    { label: "Asociación:", value: selectedLawyer?.association || "-" },
  ];

  // console.log(clientData);
  const dataClient = [
    {
      label: "Cliente:",
      value: clientData?.name && (
        <NavLink
          style={{ textDecoration: "none", color: COLORS.client }}
          to={`/clientes/ver?id=${clientData.id}`}
        >
          <Box component="span" sx={{ fontWeight: 700, color: COLORS.client }}>
            {clientData.name}
          </Box>
        </NavLink>
      ),
    },
    { label: "Documento:", value: clientData?.document || "-" },
    { label: "Teléfono:", value: clientData?.phone || "-" },
    { label: "Email:", value: clientData?.email || "-" },
    { label: "Dirección:", value: clientData?.address || "-" },
    { label: "Provincia:", value: clientData?.province || "-" },
    { label: "Código postal:", value: clientData?.postalcode || "-" },
  ];

  const flowById = new Map(
    (dataCase?.completeFlow || []).map((flow) => [Number(flow?.id), flow]),
  );

  const resolveLatestCaseTask = () => {
    const candidateTasks = [...(dataCase?.tasks || [])];

    if (candidateTasks.length > 0) {
      const sortedTasks = candidateTasks.sort((a, b) => {
        const aOrder = Number(flowById.get(Number(a?.task_id))?.order ?? 0);
        const bOrder = Number(flowById.get(Number(b?.task_id))?.order ?? 0);

        if (bOrder !== aOrder) {
          return bOrder - aOrder;
        }

        const aDate = a?.date_update || a?.date_in || a?.date_end || "";
        const bDate = b?.date_update || b?.date_in || b?.date_end || "";

        const aTime = aDate ? new Date(aDate).getTime() : 0;
        const bTime = bDate ? new Date(bDate).getTime() : 0;

        return bTime - aTime;
      });

      const latestTask = sortedTasks[0];
      const flowTask = flowById.get(Number(latestTask?.task_id));

      return {
        status: Number(latestTask?.status ?? 0),
        status_name: flowTask?.status_name || dataCase?.flow?.status_name || "",
        order: Number(flowTask?.order || 0),
      };
    }

    const backendTask = dataCase?.latest_task || dataCase?.task;

    if (backendTask) {
      const flowTask = flowById.get(Number(backendTask?.task_id));

      return {
        status: Number(backendTask?.status ?? 0),
        status_name: flowTask?.status_name || dataCase?.flow?.status_name || "",
        order: Number(flowTask?.order || 0),
      };
    }

    return undefined;
  };

  const latestCaseTask = resolveLatestCaseTask();

  const displayedTaskStatus = latestCaseTask?.status ?? 0;
  const displayedStatusName =
    latestCaseTask?.status_name || dataCase?.flow?.status_name || "";

  return (
    <Grid container item xs={12} md={12} m={"auto"} sx={glassSurface}>
      <Dialog
        open={openMandatoryLawyerDialog}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Asignación obligatoria de abogado</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este expediente debe tener un abogado asignado para poder trabajar
            en él.
          </Alert>

          <Autocomplete
            options={companyLawyersList}
            value={newLawyer}
            inputValue={inputLawyerValue}
            onChange={(e, v) => setNewLawyer(v)}
            onInputChange={(e, v) => setInputLawyerValue(v)}
            getOptionLabel={(o) =>
              o ? `${o.name || ""} ${o.surname || ""}`.trim() : ""
            }
            fullWidth
            renderInput={(params) => (
              <TextField {...params} label="Seleccionar abogado" size="small" />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!newLawyer}
            onClick={handleConfirmNewLawyer}
          >
            Asignar abogado
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          p: { xs: "8px", md: "10px" },
          width: "100%",
        }}
      >
        {/* Alerta */}
        {contactConditionIfExist && (
          <Alert
            variant={
              flagVip
                ? "standard"
                : contactConditionIfExist.includes("multiples contactos")
                  ? "outlined"
                  : "filled"
            }
            severity={
              contactConditionIfExist.includes("multiples contactos")
                ? "error"
                : "info"
            }
            icon={flagVip ? false : <Info />}
            sx={{
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              display: flagVip ? "block" : "flex",
              width: "auto",
              borderRadius: "10px",
              mb: 1,

              ...(flagVip && {
                background: "linear-gradient(135deg, #0f0f0f, #1a1a1a)",
                border: "1px solid rgba(255,215,0,0.2)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                overflow: "hidden",

                "& .MuiAlert-message": {
                  width: "100%",
                  padding: 0,
                },
              }),
            }}
          >
            {flagVip ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "5px 10px",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "radial-gradient(circle, rgba(255,215,0,0.9), rgba(255,215,0,0.2))",
                      boxShadow: "0px 0px 28px 6px rgba(255,215,0,0.4)",
                    }}
                  >
                    <Star sx={{ color: "#000" }} />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: "#FFD700",
                        fontWeight: 700,
                        fontSize: "14px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      CONDICIÓN ESPECIAL ACTIVA
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "13px",
                      }}
                    >
                      {contactConditionIfExist}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "13px",
                    background: "linear-gradient(135deg, #FFD700, #FFB800)",
                    color: "#000",
                    boxShadow: "0 4px 12px rgba(255,215,0,0.4)",
                  }}
                >
                  VIP
                </Box>
              </Box>
            ) : (
              contactConditionIfExist
            )}
          </Alert>
        )}
        {/* Header */}
        <Grid
          container
          alignItems="center"
          sx={{
            mb: 1,
            textAlign: "center",
            p: { xs: 1.25, md: 1.5 },
            background: "#f8fafc",
            borderRadius: "10px",
            border: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          {/* IZQUIERDA — TIPO DE CASO */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: COLORS.subtleText,
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  lineHeight: 1.2,
                }}
              >
                {dataCase?.case_type_name}
              </Typography>
            </Box>
          </Grid>

          {/* CENTRO — EXPEDIENTE */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: { xs: 1, md: 0 },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  letterSpacing: 0.5,
                  mr: "10px",
                }}
              >
                EXPEDIENTE #{String(dataCase?.case_id || "-").toUpperCase()}
              </Box>

              <IconButton size="small">
                <Inventory2 />
              </IconButton>
            </Box>
          </Grid>

          {/* DERECHA — ÚLTIMA ACTUALIZACIÓN */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "right" }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Box
                  component="span"
                  variant="span"
                  sx={{
                    fontSize: 12,
                    color: COLORS.subtleText,
                    fontWeight: "600",
                  }}
                >
                  Última actualización
                </Box>
                <CustomIcons icon={AccessTime} width="15px" height="15px" />
                <Box component="span" sx={{ fontSize: 12 }}>
                  {dataCase?.last_update || "-"}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        {/* Estado */}
        <Box
          sx={{
            ...STYLES.sectionCard,
            mb: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "0 4px 14px rgba(15,23,42,0.05)",
          }}
        >
          {/* <Box component="span" sx={{ fontWeight: 700 }}>Estado</Box> */}
          <Box sx={{ ml: 1 }}>
            {(() => {
              const config = statusConfig[displayedTaskStatus];
              return (
                <CustomIconsPlusText
                  icon={config?.icon || Close}
                  severity={config?.severity}
                  variant={config?.variant}
                  text={
                    config
                      ? config.text(displayedStatusName).toUpperCase()
                      : "No iniciado"
                  }
                />
              );
            })()}
          </Box>
        </Box>
        {/* Abogado + Cliente */}
        <Grid container spacing={1}>
          {/* Abogado */}
          <Grid item xs={12} md={12} lg={6}>
            <Box
              sx={{
                ...STYLES.sectionCard,
                height: "-webkit-fill-available",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
              }}
            >
              <Grid container alignItems="center">
                <Grid
                  item
                  xs={3}
                  sx={{ justifyContent: "center", textAlign: "center" }}
                >
                  <Box
                    sx={{
                      ...STYLES.avatarWrapper,
                      background: COLORS.lawyerBg,
                      margin: "auto",
                      mb: "10px",
                    }}
                  >
                    <Work sx={{ color: COLORS.lawyer, fontSize: 70 }} />
                  </Box>
                  <Box
                    sx={{
                      ...STYLES.pill,
                      background: COLORS.lawyer,
                      text: "auto",
                    }}
                  >
                    Abogado
                  </Box>
                </Grid>

                <Grid item xs={9}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      mb: "5px",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <CustomButtonPlusIcon
                        variant="outlined"
                        color="info"
                        position="start"
                        size="small"
                        icon={People}
                        handleFunction={() => {
                          setChangeLawyer((prev) => !prev);
                          setNewLawyer(selectedLawyer || null);
                        }}
                        text={"Editar Abogado"}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  {/* Campos */}
                  <Grid container sx={{ mt: 1 }}>
                    {dataLawyer.map((item, index) => (
                      <Grid
                        key={index}
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "7px",
                        }}
                      >
                        {/* LABEL */}
                        <Box
                          sx={{
                            width: "25%", // ajustable
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: COLORS.subtleText,
                              fontSize: 14,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.label.replace(":", "")}
                          </Box>
                        </Box>

                        {/* VALUE / AUTOCOMPLETE */}
                        <Box
                          sx={{
                            width: "75%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {index === 0 && changeLawyer ? (
                            <Box
                              sx={{
                                ...STYLES.autoWrapper,
                                height: "20px",
                                alignItems: "center",
                                display: "flex",
                                gap: "4px",
                                width: "100%",
                              }}
                            >
                              <Autocomplete
                                options={companyLawyersList}
                                value={newLawyer}
                                inputValue={inputLawyerValue}
                                onChange={(e, v) => setNewLawyer(v)}
                                onInputChange={(e, v) => setInputLawyerValue(v)}
                                getOptionLabel={(o) =>
                                  o
                                    ? `${o.name || ""} ${o.surname || ""}`.trim()
                                    : ""
                                }
                                sx={{ flex: 1 }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Buscar abogado"
                                    size="small"
                                    sx={{
                                      "& .MuiInputBase-root": {
                                        height: "20px",
                                        fontSize: 12,
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                      },
                                    }}
                                  />
                                )}
                              />

                              <IconButton
                                onClick={handleCancel}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  bgcolor: "#fff",
                                  borderRadius: "10px",
                                  border: "1px solid rgba(0,0,0,0.1)",
                                  p: 0,
                                  "& .MuiSvgIcon-root": { fontSize: 14 },
                                }}
                              >
                                <Close sx={{ color: "#d32f2f" }} />
                              </IconButton>

                              <IconButton
                                disabled={!newLawyer}
                                onClick={handleConfirmNewLawyer}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  bgcolor: newLawyer
                                    ? COLORS.primary
                                    : "rgba(0,0,0,0.05)",
                                  borderRadius: "10px",
                                  p: 0,
                                  "& .MuiSvgIcon-root": { fontSize: 14 },
                                  color: newLawyer ? "#fff" : "#999",
                                }}
                              >
                                <Check />
                              </IconButton>
                            </Box>
                          ) : isRefreshingLawyerInfo ? (
                            <Box
                              sx={{
                                height: 22,
                                minHeight: 22,
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <CircularProgress size={16} thickness={5} />
                            </Box>
                          ) : (
                            <Box
                              component="span"
                              sx={{
                                color: COLORS.subtleText,
                                fontSize: 14,
                                fontWeight: 600,
                                minHeight: 22,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {item.value}
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Cliente */}
          <Grid item xs={12} md={12} lg={6}>
            <Box
              sx={{
                ...STYLES.sectionCard,
                border: "1px solid rgba(15, 23, 42, 0.08)",
                boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
              }}
            >
              <Grid container alignItems="center">
                <Grid
                  item
                  xs={3}
                  sx={{ justifyContent: "center", textAlign: "center" }}
                >
                  <Box
                    sx={{
                      ...STYLES.avatarWrapper,
                      background: COLORS.clientBg,
                      margin: "auto",
                      mb: "10px",
                    }}
                  >
                    <AccountBox sx={{ color: COLORS.client, fontSize: 70 }} />
                  </Box>
                  <Box
                    sx={{
                      ...STYLES.pill,
                      background: COLORS.client,
                      textAlign: "center",
                    }}
                  >
                    Cliente
                  </Box>
                </Grid>

                <Grid item xs={9}>
                  <Grid container>
                    {dataClient.map((item, index) => (
                      <Grid
                        key={index}
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "7px",
                        }}
                      >
                        {/* LABEL */}
                        <Box
                          sx={{
                            width: "25%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: COLORS.subtleText,
                              fontSize: 14,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.label.replace(":", "")}
                          </Box>
                        </Box>

                        {/* VALUE */}
                        <Box
                          sx={{
                            width: "75%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {item.value ? (
                            <Box
                              component="span"
                              sx={{
                                color: COLORS.subtleText,
                                fontSize: 14,
                              }}
                            >
                              {item.value}
                            </Box>
                          ) : (
                            <Box
                              component="span"
                              sx={{
                                color: COLORS.client,
                                fontSize: 14,
                              }}
                            >
                              {" "}
                              Información no disponible
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default InfoSection;
